<!--
  기존 계정 강사 권한 요청 화면
  · POST /instructor/access-request (Bearer 필요 — 미로그인 시 전역 가드가 로그인 유도)
  · 이메일은 서버가 토큰에서 추출하므로 readonly 표시만 (전송하지 않음)
  · 진입 시 GET /instructor/access-request/me 선조회로 분기
    (이미 강사 / 대기중 요청 존재 → 상태 화면, 거절 이력 → 사유 배너 + 재신청 폼)
  · route meta.layout='empty' — 독립 카드 레이아웃
-->
<template>
  <div class="p-access">
    <div class="p-access__card">
      <!-- 로고 -->
      <div class="p-access__logo">
        <img src="/favicon.png" alt="Pineapple" />
        <div>
          <div class="p-access__brand">pineapple</div>
          <div class="p-access__brand-sub">INSTRUCTOR</div>
        </div>
      </div>

      <template v-if="checking">
        <div class="p-access__loading">계정 상태를 확인하는 중입니다…</div>
      </template>

      <!-- 이미 강사인 계정 -->
      <template v-else-if="alreadyInstructor">
        <a-pill kind="ok" class="p-access__pill">강사 활성</a-pill>
        <h1 class="p-access__title">이미 강사 계정입니다</h1>
        <p class="p-access__desc">별도의 권한 요청 없이 강사 메뉴를 이용하실 수 있습니다.</p>
        <a-btn kind="primary" class="p-access__cta" @click="$router.push({ name: 'Sales' })">대시보드로 이동</a-btn>
      </template>

      <!-- 요청 폼 -->
      <template v-else>
        <h1 class="p-access__title">강사 권한 요청</h1>
        <p class="p-access__desc">
          강사 인증 정보를 입력해주세요. 관리자 확인 후<br />
          승인 결과를 메일로 안내드립니다.
        </p>

        <!-- 이전 요청 거절 배너 -->
        <div v-if="rejectReason" class="p-access__reason">
          <div class="p-access__reason-label">이전 요청이 거절되었습니다</div>
          <div class="p-access__reason-text">{{ rejectReason }}</div>
        </div>

        <vee-form
          ref="formRef"
          :validation-schema="schema"
          :initial-values="initialValues"
          class="p-access-form"
          @submit="onSubmit"
        >
          <div class="p-access-form__row">
            <field v-slot="{ field, errors }" name="name">
              <div class="p-access-form__field">
                <label class="p-access-form__label">이름 <em>*</em></label>
                <input v-bind="field" type="text" name="name" class="p-input" placeholder="홍길동" :class="{ 'is-invalid': errors.length > 0 }" />
                <error-message name="name" class="p-access-form__err" />
              </div>
            </field>

            <field v-slot="{ field, errors }" name="phone">
              <div class="p-access-form__field">
                <label class="p-access-form__label">휴대폰 번호 <em>*</em></label>
                <input v-bind="field" type="tel" name="phone" class="p-input" placeholder="01012345678 (숫자만)" :class="{ 'is-invalid': errors.length > 0 }" />
                <error-message name="phone" class="p-access-form__err" />
              </div>
            </field>
          </div>

          <div class="p-access-form__field">
            <label class="p-access-form__label">이메일</label>
            <input :value="userEmail" type="email" class="p-input" readonly disabled />
            <div class="p-access-form__hint">로그인 계정의 이메일로 요청됩니다.</div>
          </div>

          <div class="p-access-form__row">
            <field v-slot="{ field, errors }" name="orgName">
              <div class="p-access-form__field">
                <label class="p-access-form__label">소속 기관명 <em>*</em></label>
                <input v-bind="field" type="text" name="orgName" class="p-input" placeholder="OO어학원" :class="{ 'is-invalid': errors.length > 0 }" />
                <error-message name="orgName" class="p-access-form__err" />
              </div>
            </field>

            <field v-slot="{ field, errors }" name="position">
              <div class="p-access-form__field">
                <label class="p-access-form__label">직위 <em>*</em></label>
                <input v-bind="field" type="text" name="position" class="p-input" placeholder="강사, 원장 등" :class="{ 'is-invalid': errors.length > 0 }" />
                <error-message name="position" class="p-access-form__err" />
              </div>
            </field>
          </div>

          <field v-slot="{ field }" name="subject">
            <div class="p-access-form__field">
              <label class="p-access-form__label">담당 과목 · 교재명</label>
              <input v-bind="field" type="text" name="subject" class="p-input" placeholder="예) 영어 / OO교재" />
            </div>
          </field>

          <field v-slot="{ field }" name="reason">
            <div class="p-access-form__field">
              <label class="p-access-form__label">요청 사유</label>
              <textarea v-bind="field" name="reason" class="p-input p-access-form__textarea" rows="3" placeholder="강사 권한이 필요한 사유를 입력해주세요 (선택)" />
            </div>
          </field>

          <label class="p-access-form__consent">
            <input v-model="privacyConsent" type="checkbox" />
            <span>
              <b>[필수]</b> 개인정보 수집·이용에 동의합니다.
              <span class="p-access-form__consent-sub">입력하신 정보는 강사 자격 확인 목적으로만 사용됩니다.</span>
            </span>
          </label>

          <a-btn type="submit" kind="primary" class="p-access-form__submit" :disabled="!privacyConsent || submitting">
            {{ submitting ? '제출 중…' : '강사 권한 요청' }}
          </a-btn>
        </vee-form>
      </template>

      <!-- 하단 -->
      <div class="p-access__footer">
        <router-link class="p-access__link" :to="{ name: 'InstructorAccessStatus' }">요청 상태 확인</router-link>
        <a class="p-access__link" href="#" @click.prevent="logout">로그아웃</a>
      </div>
    </div>
  </div>
</template>

<script>
import { Field, Form as VeeForm, ErrorMessage } from 'vee-validate';
import * as yup from 'yup';
import ABtn from '@/components/Pineapple/ABtn.vue';
import APill from '@/components/Pineapple/APill.vue';

export default {
  name: 'InstructorAccessRequest',
  components: { Field, VeeForm, ErrorMessage, ABtn, APill },
  data() {
    return {
      checking: true,
      alreadyInstructor: false,
      rejectReason: '',
      privacyConsent: false,
      submitting: false,
      schema: yup.object({
        name: yup.string().trim().required('이름을 입력해주세요'),
        phone: yup
          .string()
          .required('휴대폰 번호를 입력해주세요')
          .test('digits', '숫자만 9~11자리로 입력해주세요', (v) =>
            /^\d{9,11}$/.test(String(v || '').replace(/[-\s]/g, ''))
          ),
        orgName: yup.string().trim().required('소속 기관명을 입력해주세요'),
        position: yup.string().trim().required('직위를 입력해주세요'),
        subject: yup.string(),
        reason: yup.string(),
      }),
    };
  },
  computed: {
    userInfo() {
      return this.$store.getters['auth/getUserInfo'] || {};
    },
    userEmail() {
      return this.userInfo.email || '';
    },
    initialValues() {
      return { name: this.userInfo.name || '' };
    },
  },
  mounted() {
    this.precheck();
  },
  methods: {
    async precheck() {
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/instructor/access-request/me',
        });
        if (res.data.user_type === 'instructor') {
          this.alreadyInstructor = true;
        } else if (res.data.request?.status === 'request') {
          // 이미 대기중인 요청 존재 → 상태 화면으로
          this.$router.replace({ name: 'InstructorAccessStatus' });
          return;
        } else if (res.data.request?.status === 'rejection') {
          this.rejectReason = res.data.request.reject_reason || '';
        }
      } catch (e) {
        // 선조회 실패 시에도 폼은 노출 (제출 시점에 서버가 재검증)
        console.error('요청 상태 선조회 실패:', e);
      } finally {
        this.checking = false;
      }
    },
    async onSubmit(values) {
      if (!this.privacyConsent || this.submitting) return;
      this.submitting = true;
      try {
        await this.$axios({
          method: 'post',
          url: '/instructor/access-request',
          data: {
            name: values.name.trim(),
            phone: String(values.phone).replace(/[-\s]/g, ''),
            orgName: values.orgName.trim(),
            position: values.position.trim(),
            subject: values.subject?.trim() || '',
            reason: values.reason?.trim() || '',
            privacyConsent: true,
            signupPath: 'existing',
          },
          loadingBar: true,
        });
        this.$notifyVue({ type: 'success', title: '강사 권한 요청', text: '요청이 접수되었습니다. 관리자 확인 후 메일로 안내드립니다.' });
        this.$router.push({ name: 'InstructorAccessStatus' });
      } catch (e) {
        const status = e.response?.status;
        if (status === 409) {
          await this.$swal({ icon: 'info', title: '처리 대기중', text: '이미 처리 대기중인 요청이 있습니다. 관리자 확인을 기다려주세요.' });
          this.$router.push({ name: 'InstructorAccessStatus' });
        } else {
          const msg = e.response?.data?.message || e.response?.data?.detail || '요청 접수에 실패했습니다. 잠시 후 다시 시도해주세요.';
          this.$swal({ icon: 'error', title: '요청 실패', text: msg });
        }
      } finally {
        this.submitting = false;
      }
    },
    logout() {
      this.$store.dispatch('auth/logout');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';
@import './access-shared.scss';

.p-access-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.p-access-form__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
}
.p-access-form__field {
  display: flex;
  flex-direction: column;
}
.p-access-form__label {
  font-size: 12px;
  font-weight: 700;
  color: $p-ink;
  margin-bottom: 6px;

  em {
    color: $p-err;
    font-style: normal;
  }
}
.p-input {
  width: 100%;
  height: 42px;
  padding: 0 14px;
  border: 1px solid $p-line;
  border-radius: 8px;
  background: $p-panel;
  font-family: $p-font-body;
  font-size: 13px;
  color: $p-ink;
  outline: none;

  &:focus { border-color: $p-point; box-shadow: 0 0 0 3px rgba(255, 201, 60, 0.2); }
  &.is-invalid { border-color: $p-err; }
  &::placeholder { color: $p-ink-soft; }
  &:disabled { background: $p-line-soft; color: $p-ink-mid; cursor: not-allowed; }
}
.p-access-form__textarea {
  height: auto;
  padding: 10px 14px;
  resize: vertical;
  line-height: 1.5;
}
.p-access-form__err {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: $p-err;
}
.p-access-form__hint {
  margin-top: 4px;
  font-size: 11px;
  color: $p-ink-soft;
}
.p-access-form__consent {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: $p-ink-mid;
  line-height: 1.5;
  cursor: pointer;
  padding: 12px 14px;
  border: 1px solid $p-line;
  border-radius: 10px;
  background: $p-bg;

  input {
    margin-top: 2px;
    accent-color: $p-point-deep;
    cursor: pointer;
  }
  b { color: $p-ink; }
}
.p-access-form__consent-sub {
  display: block;
  font-size: 11px;
  color: $p-ink-soft;
  margin-top: 2px;
}
.p-access-form__submit {
  height: 44px;
  font-size: 13px;
  justify-content: center;
  margin-top: 4px;
}
</style>
